import { lazy } from 'react';

/**
 * lazyRetry — Wraps React.lazy() with automatic retry + hard reload on chunk load failures.
 *
 * When a new deploy changes JS chunk hashes, the old import() paths become 404s.
 * This utility:
 * 1. Retries the import up to `retries` times
 * 2. If all retries fail, forces a page reload (once) to load the new index.html
 * 3. Uses sessionStorage to prevent infinite reload loops
 * 4. Exposes a `.preload()` method on the returned component so the caller
 *    can warm the chunk on hover/focus (see `routePrefetch.js`). Repeated
 *    calls are cheap because webpack memoizes resolved dynamic imports.
 *
 * Usage:
 *   const MyComponent = lazyRetry(() => import('./MyComponent'));
 *   MyComponent.preload();
 */
const lazyRetry = (importFn, retries = 2) => {
  let inflight = null;
  const load = () => {
    if (inflight) return inflight;
    const attempt = (retriesLeft) =>
      importFn().catch((error) => {
        if (retriesLeft > 0) {
          return new Promise((resolve) => setTimeout(resolve, 500)).then(() =>
            attempt(retriesLeft - 1),
          );
        }

        const hasReloaded = sessionStorage.getItem('chunk_reload_attempted');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload_attempted', 'true');
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath && currentPath !== '/' && currentPath !== '/login') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          }
          window.location.reload();
          return new Promise(() => {});
        }

        sessionStorage.removeItem('chunk_reload_attempted');
        inflight = null;
        throw error;
      });

    inflight = attempt(retries).then((module) => {
      sessionStorage.removeItem('chunk_reload_attempted');
      return module;
    });
    return inflight;
  };

  const Component = lazy(load);
  Component.preload = load;
  return Component;
};

export default lazyRetry;
